import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import MainLayout from '../../components/mainLayout/MainLayout';
import ExpedientDetail from '../../components/expedientDetail/ExpedientDetail';
import styles from './DocumentValidator.module.css';
import { getDocumentTypeName, INVALID_QR_MESSAGE } from './documentType';

const DocumentValidator = (props) => {
  const { token } = useParams();
  const history = useHistory();
  const [isValidToken, setIsValidToken] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [documentData, setDocumentData] = useState(null);
  const [errorTitle, setErrorTitle] = useState(INVALID_QR_MESSAGE);
  const [errorMessage, setErrorMessage] = useState(
    'El documento no pudo ser validado. Verifique el enlace.'
  );

  useEffect(() => {
    if (!token) {
      history.replace('/');
      return;
    }
    validateToken(token);
  }, [token, history]);

  const validateToken = async (tokenToValidate) => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_URL_API_LARAVEL}/document/validate/${tokenToValidate}`
      );
      const data = await response.json();

      if (data.status && data.data) {
        setIsValidToken(true);
        setDocumentData({
          // Información del expediente
          expedientNumber: data.data.document.expedient_number,
          court: data.data.document.court,
          judicialParty: data.data.document.judicial_party,
          judgmentType: data.data.document.kind_judgment,
          matter: data.data.document.matter,
          via: data.data.document.via,
          actor: data.data.document.actor,
          defendant: data.data.document.defendant,
          isSecondInstance: data.data.document.is_second_instance || false,

          // Información del documento
          documentName: data.data.document.alias || data.data.document.filename,
          documentType: data.data.document.document_type,
          judgmentSubtype: data.data.document.judgment_type,
          notificationDelivery: data.data.document.notification_delivery,
          creationDate: data.data.document.created_at,
          signDate: data.data.document.created_at,

          // Firmantes
          signersList: data.data.document.signers_list || [],

          // Fechas de validación
          validatedAt: data.data.validated_at,
        });
      } else {
        // Every rejected QR shares the same title; the backend message tells unreadable,
        // tampered and unknown QRs apart.
        setIsValidToken(false);
        setErrorTitle(INVALID_QR_MESSAGE);
        setErrorMessage(
          (data && data.data && data.data.message) ||
            'El documento no pudo ser validado. Verifique el enlace.'
        );
      }
    } catch (error) {
      console.error('Error validating token:', error);
      setIsValidToken(false);
      setErrorTitle('Sin conexión con el servidor');
      setErrorMessage(
        'No fue posible contactar al servicio de validación. Revise su conexión e intente de nuevo.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('es-MX', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className={styles.container}>
          <div className={styles.loading}>Validando documento...</div>
        </div>
      </MainLayout>
    );
  }

  if (!isValidToken) {
    return (
      <MainLayout>
        <div className={styles.container}>
          <div className={styles.error}>
            <h2>{errorTitle}</h2>
            <p>{errorMessage}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className={styles.container}>
        <div className={styles.validationCard}>
          <h1 className={styles.title}>Verificación de Autenticidad</h1>

          <div className={styles.documentInfo}>
            {/* Información del Expediente */}
            <div className={styles.expedientDetails}>
              <div className={styles.expedientDetails_title}>
                {documentData.isSecondInstance ? 'TOCA' : 'Expediente'}: L
                {documentData.expedientNumber}
              </div>
              <div className={styles.expedientDetails_details}>
                <ExpedientDetail title={documentData.isSecondInstance ? 'Sala' : 'Juzgado'}>
                  {documentData.court}
                </ExpedientDetail>
                <ExpedientDetail
                  title={documentData.isSecondInstance ? 'Dependencia' : 'Partido Judicial'}
                >
                  {documentData.judicialParty}
                </ExpedientDetail>
                <ExpedientDetail title={'Tipo de juicio'}>
                  {documentData.judgmentType}
                </ExpedientDetail>
                <ExpedientDetail title={'Materia'}>{documentData.matter}</ExpedientDetail>
                <ExpedientDetail title={'Vía'}>{documentData.via}</ExpedientDetail>
              </div>
            </div>

            {/* Información del Documento */}
            <div className={styles.expedientDetails}>
              <div className={styles.expedientDetails_title}>
                {getDocumentTypeName(
                  documentData.documentType,
                  documentData.judgmentSubtype,
                  documentData.notificationDelivery
                )}
              </div>
              <div className={styles.expedientDetails_details}>
                <ExpedientDetail title={'Nombre'}>{documentData.documentName}</ExpedientDetail>
                <ExpedientDetail
                  title={
                    documentData.isSecondInstance
                      ? 'Fecha de creación del documento'
                      : 'Fecha de creación'
                  }
                >
                  {formatDate(documentData.creationDate)}
                </ExpedientDetail>
                <ExpedientDetail title={'Fecha de Firma'}>
                  {formatDate(documentData.signDate)}
                </ExpedientDetail>
              </div>
            </div>
          </div>

          {/* Sección de Firmantes */}
          {documentData.signersList && documentData.signersList.length > 0 && (
            <div className={styles.signersSection}>
              <h3 className={styles.signersTitle}>
                {documentData.isSecondInstance ? 'Nombre del firmante' : 'Firmantes:'}
              </h3>
              <div className={styles.signersList}>
                {documentData.signersList.map((signer, index) => (
                  <div key={index} className={styles.signer}>
                    {signer}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Estado de Validación */}
          <div className={styles.validationStatus}>
            <div className={styles.checkIcon}>✓</div>
            <p className={styles.validMessage}>
              El documento es válido y se encuentra registrado en nuestro sistema.
            </p>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default DocumentValidator;
